/**
 * Keyboard, gamepad and touch → one normalised control state per frame
 * (design.md B11). Quantised later by the sim so replays are exact.
 */

export interface Controls {
  steer: number; // -1 … 1
  throttle: number; // 0 … 1
  brake: number; // 0 … 1
}

export type Action = 'start' | 'pause' | 'restart' | 'menu' | 'left' | 'right' | 'up' | 'down' | 'fullscreen' | 'mute' | 'camera';

/**
 * One player's controller in Head-to-Head (design.md B8.1, B11): a half of
 * the keyboard, a gamepad (by index), or this device's touch controls.
 */
export type Source = 'wasd' | 'arrows' | 'touch' | `pad${number}`;

export function sourceLabel(s: Source): string {
  if (s === 'wasd') return 'KEYS W A S D';
  if (s === 'arrows') return 'ARROW KEYS';
  if (s === 'touch') return 'TOUCH';
  return `PAD ${Number(s.slice(3)) + 1}`;
}

const SPLIT: Record<'wasd' | 'arrows', { left: string; right: string; gas: string; brake: string }> = {
  wasd: { left: 'KeyA', right: 'KeyD', gas: 'KeyW', brake: 'KeyS' },
  arrows: { left: 'ArrowLeft', right: 'ArrowRight', gas: 'ArrowUp', brake: 'ArrowDown' },
};

const STEER_LEFT = new Set(['ArrowLeft', 'KeyA']);
const STEER_RIGHT = new Set(['ArrowRight', 'KeyD']);
const GAS = new Set(['ArrowUp', 'KeyW', 'KeyZ', 'Space', 'ShiftLeft', 'ShiftRight']);
const BRAKE = new Set(['ArrowDown', 'KeyS', 'KeyX']);
const GAME_KEYS = new Set([...STEER_LEFT, ...STEER_RIGHT, ...GAS, ...BRAKE, 'Enter', 'Escape', 'KeyP', 'KeyR', 'KeyF', 'KeyM', 'KeyC']);

export class InputManager {
  private keys = new Set<string>();
  private touch = { left: false, right: false, gas: false, brake: false };
  private padPrev: boolean[][] = [];
  private listeners: ((a: Action) => void)[] = [];
  private joinListeners: ((s: Source) => void)[] = [];
  /** On touch screens the car accelerates by itself unless braking (B11). */
  autoAccelerate = false;
  touchActive = false;
  padConnected = false;

  constructor() {
    window.addEventListener('keydown', (e) => {
      if (GAME_KEYS.has(e.code)) e.preventDefault();
      if (e.repeat) return;
      this.keys.add(e.code);
      this.touchActive = false;
      if (e.code === 'KeyW') this.emitJoin('wasd');
      if (e.code === 'ArrowUp') this.emitJoin('arrows');
      if (e.code === 'Enter' || (GAS.has(e.code) && e.code !== 'ArrowUp' && e.code !== 'KeyW')) this.emit('start');
      if (e.code === 'ArrowUp' || e.code === 'KeyW') this.emit('up');
      if (e.code === 'ArrowDown' || e.code === 'KeyS') this.emit('down');
      if (e.code === 'KeyP' || e.code === 'Escape') this.emit('pause');
      if (e.code === 'KeyR') this.emit('restart');
      if (e.code === 'KeyF') this.emit('fullscreen');
      if (e.code === 'KeyM') this.emit('mute');
      if (e.code === 'KeyC') this.emit('camera');
      if (STEER_LEFT.has(e.code)) this.emit('left');
      if (STEER_RIGHT.has(e.code)) this.emit('right');
    });
    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
    });
    window.addEventListener('blur', () => this.keys.clear());
    window.addEventListener('gamepadconnected', () => (this.padConnected = true));
    window.addEventListener('gamepaddisconnected', () => (this.padConnected = navigator.getGamepads().some((p) => p)));
  }

  onAction(fn: (a: Action) => void): void {
    this.listeners.push(fn);
  }

  emit(a: Action): void {
    for (const fn of this.listeners) fn(a);
  }

  /** "Press gas to join": fires with the controller that pressed it. */
  onJoin(fn: (s: Source) => void): void {
    this.joinListeners.push(fn);
  }

  emitJoin(s: Source): void {
    for (const fn of this.joinListeners) fn(s);
  }

  /** One player's controls (Head-to-Head). */
  readSource(src: Source): Controls {
    let steer = 0;
    let throttle = 0;
    let brake = 0;
    if (src === 'wasd' || src === 'arrows') {
      const k = SPLIT[src];
      if (this.keys.has(k.left)) steer -= 1;
      if (this.keys.has(k.right)) steer += 1;
      if (this.keys.has(k.gas)) throttle = 1;
      if (this.keys.has(k.brake)) brake = 1;
    } else if (src === 'touch') {
      if (this.touch.left) steer -= 1;
      if (this.touch.right) steer += 1;
      if (this.touch.gas) throttle = 1;
      if (this.touch.brake) brake = 1;
      if (this.autoAccelerate && !this.touch.brake) throttle = 1;
    } else {
      const p = navigator.getGamepads ? navigator.getGamepads()[Number(src.slice(3))] : null;
      if (p) {
        const ax = p.axes[0] ?? 0;
        if (Math.abs(ax) > 0.18) steer += (ax - Math.sign(ax) * 0.18) / 0.82;
        if (p.buttons[14]?.pressed) steer -= 1;
        if (p.buttons[15]?.pressed) steer += 1;
        throttle = Math.max(p.buttons[7]?.value ?? 0, p.buttons[0]?.pressed ? 1 : 0);
        brake = Math.max(p.buttons[6]?.value ?? 0, p.buttons[1]?.pressed ? 1 : 0);
      }
    }
    return { steer: Math.max(-1, Math.min(1, steer)), throttle, brake };
  }

  /** Wire an on-screen touch button (pointer events; multi-touch safe). */
  bindTouch(el: HTMLElement, key: keyof InputManager['touch']): void {
    const on = (e: PointerEvent) => {
      e.preventDefault();
      this.touchActive = true;
      this.touch[key] = true;
      el.classList.add('down');
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };
    const off = (e: PointerEvent) => {
      e.preventDefault();
      this.touch[key] = false;
      el.classList.remove('down');
    };
    el.addEventListener('pointerdown', on);
    el.addEventListener('pointerup', off);
    el.addEventListener('pointercancel', off);
    el.addEventListener('lostpointercapture', () => {
      this.touch[key] = false;
      el.classList.remove('down');
    });
  }

  read(): Controls {
    let steer = 0;
    let throttle = 0;
    let brake = 0;

    for (const k of this.keys) {
      if (STEER_LEFT.has(k)) steer -= 1;
      if (STEER_RIGHT.has(k)) steer += 1;
      if (GAS.has(k)) throttle = 1;
      if (BRAKE.has(k)) brake = 1;
    }

    if (this.touch.left) steer -= 1;
    if (this.touch.right) steer += 1;
    if (this.touch.gas) throttle = 1;
    if (this.touch.brake) brake = 1;
    if (this.touchActive && this.autoAccelerate && !this.touch.brake) throttle = 1;

    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    let drove = false;
    for (let pi = 0; pi < pads.length; pi++) {
      const p = pads[pi];
      if (!p) continue;
      this.padConnected = true;
      const ax = p.axes[0] ?? 0;
      const b = (i: number) => p.buttons[i]?.value ?? 0;
      const pressed = (i: number) => !!p.buttons[i]?.pressed;
      // The first pad drives in single-player modes; every pad works the menus.
      if (!drove) {
        drove = true;
        if (Math.abs(ax) > 0.18) steer += (ax - Math.sign(ax) * 0.18) / 0.82;
        if (pressed(14)) steer -= 1;
        if (pressed(15)) steer += 1;
        throttle = Math.max(throttle, b(7), pressed(0) ? 1 : 0);
        brake = Math.max(brake, b(6), pressed(1) ? 1 : 0);
      }
      // Edge-triggered menu buttons.
      const now = [pressed(9), pressed(0), pressed(8), pressed(14), pressed(15), pressed(3), pressed(12), pressed(13), pressed(7)];
      const acts: (Action | null)[] = ['pause', 'start', 'restart', 'left', 'right', 'restart', 'up', 'down', null];
      const prev = this.padPrev[pi] ?? [];
      now.forEach((v, i) => {
        if (v && !prev[i]) {
          const a = acts[i];
          if (a) this.emit(a);
          if (i === 1 || i === 8) this.emitJoin(`pad${pi}`);
        }
      });
      this.padPrev[pi] = now;
      if (now.some(Boolean) || Math.abs(ax) > 0.5) this.touchActive = false;
    }

    return { steer: Math.max(-1, Math.min(1, steer)), throttle, brake };
  }
}
